import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Stack,
  Typography,
} from "@mui/joy";
import to from "await-to-js";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";

import {
  isSignupAllowedPlan,
  isValidPaymentPeriod,
  useSignupPlanStore,
} from "../stores/signup-plan-store";
import { getAfterAuthPageRedirect } from "../utils/helpers";
import { AuthApiService } from "~/common/api/services/auth-api-service";
import {
  signUpSchema,
  type SignUpFormData,
} from "~/common/api/validation/auth-schemas";
import { RoutePaths } from "~/common/constants/route-paths";

const authApiService = new AuthApiService();

/**
 * SignUp is a React functional component that renders a registration form for user sign-up.
 */
const SignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { plan, period, setPlanAndPeriod } = useSignupPlanStore();

  // Validate plan and period from state or query params
  useEffect(() => {
    // If already have valid plan/period in state, we're good
    if (plan && period) {
      return;
    }

    // Try to get from query params
    const planParam = searchParams.get("plan");
    const periodParam = searchParams.get("period");

    if (
      planParam &&
      periodParam &&
      isSignupAllowedPlan(planParam) &&
      isValidPaymentPeriod(periodParam)
    ) {
      setPlanAndPeriod(planParam, periodParam);

      return;
    }

    // No valid plan/period - redirect to pricing
    void navigate(RoutePaths.PRICING, { replace: true });
  }, [plan, period, searchParams, setPlanAndPeriod, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
  });

  const onFormSubmit = async (data: SignUpFormData) => {
    if (!plan || !period) {
      void navigate(RoutePaths.PRICING, { replace: true });

      return;
    }

    const { passwordConfirm, ...signUpData } = data;

    if (passwordConfirm !== signUpData.password) {
      setError("root", {
        type: "manual",
        message: "Паролі не співпадають",
      });

      return;
    }

    const [error] = await to(
      authApiService.signUp({
        ...signUpData,
        plan,
        planPeriod: period,
      }),
    );

    if (error) {
      setError("root", {
        type: "manual",
        message: error.message,
      });
    } else {
      void navigate(getAfterAuthPageRedirect());
    }
  };

  // Don't render form if no valid plan/period
  if (!plan || !period) {
    return null;
  }

  return (
    <>
      <Stack sx={{ gap: 4, mb: 2 }}>
        <Stack sx={{ gap: 1 }}>
          <Typography component="h1" level="h3">
            Зареєструватись
          </Typography>
          <Typography level="body-sm">
            Вже маєте акаунт?{" "}
            <Link
              level="title-sm"
              onClick={() => void navigate(RoutePaths.SIGN_IN)}
            >
              Увійти!
            </Link>
          </Typography>
        </Stack>
      </Stack>
      <Stack sx={{ gap: 4 }}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <FormControl
              required
              sx={{ width: "100%", minWidth: "150px" }}
              error={!!errors.name}
            >
              <FormLabel>Імʼя</FormLabel>
              <Input type="text" placeholder="Іван" {...register("name")} />
              {errors.name && (
                <FormHelperText>{errors.name.message}</FormHelperText>
              )}
            </FormControl>
            <FormControl
              required
              sx={{ width: "100%", minWidth: "150px" }}
              error={!!errors.surname}
            >
              <FormLabel>Прізвище</FormLabel>
              <Input
                type="text"
                placeholder="Петренко"
                {...register("surname")}
              />
              {errors.surname && (
                <FormHelperText>{errors.surname.message}</FormHelperText>
              )}
            </FormControl>
          </Box>
          <FormControl required error={!!errors.companyName}>
            <FormLabel>Назва компанії</FormLabel>
            <Input
              type="text"
              placeholder="ТОВ Брендлі"
              {...register("companyName")}
            />
            {errors.companyName && (
              <FormHelperText>{errors.companyName.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl required error={!!errors.email}>
            <FormLabel>Електронна пошта</FormLabel>
            <Input
              type="email"
              placeholder="ivan@example.com"
              {...register("email")}
            />
            {errors.email && (
              <FormHelperText>{errors.email.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl required error={!!errors.password}>
            <FormLabel>Пароль</FormLabel>
            <Input
              type="password"
              placeholder="123456"
              {...register("password")}
            />
            {errors.password && (
              <FormHelperText>{errors.password.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl required error={!!errors.passwordConfirm}>
            <FormLabel>Підтвердіть Пароль</FormLabel>
            <Input
              type="password"
              placeholder="123456"
              {...register("passwordConfirm")}
            />
            {errors.passwordConfirm && (
              <FormHelperText>{errors.passwordConfirm.message}</FormHelperText>
            )}
          </FormControl>
          {errors.root && (
            <FormHelperText sx={{ color: "danger.500" }}>
              {errors.root.message}
            </FormHelperText>
          )}
          <Stack sx={{ gap: 4, mt: 2 }}>
            <Button type="submit" fullWidth loading={isSubmitting}>
              Зареєструватись
            </Button>
          </Stack>
        </form>
      </Stack>
    </>
  );
};

export default SignUp;
